import React, { useState } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import OptionValuesTable from './OptionValuesTable';
import FormAddEditOptionValue from './FormAddEditOptionValue';
import ModelDelete from '../../global/Models/ModelDelete';
import { useGetOptionValues } from '../../../hooks/optionGroups/optionValues/useGetOptionValues';
import { useCreateOptionValue } from '../../../hooks/optionGroups/optionValues/useCreateOptionValue';
import { useUpdateOptionValue } from '../../../hooks/optionGroups/optionValues/useUpdateOptionValue';
import { useDeleteOptionValue } from '../../../hooks/optionGroups/optionValues/useDeleteOptionValue';

/**
 * Manager component for Option Values
 * Handles CRUD operations and display for values within an Option Group
 */
const OptionValueManager = ({ optionGroup, isCreateMode = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');

    const optionGroupId = optionGroup?.id;
    const displayType = optionGroup?.display_type || 'dropdown';

    // State for Dialog (Add/Edit)
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentValue, setCurrentValue] = useState(null);

    // State for Delete Confirmation Modal
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [valueToDelete, setValueToDelete] = useState(null);

    // Hooks
    const { optionValues, isLoading } = useGetOptionValues(optionGroupId, {}, { enabled: !isCreateMode });
    const { createOptionValueMutate, isCreating } = useCreateOptionValue();
    const { editOptionValue, isUpdating } = useUpdateOptionValue();
    const { deleteOptionValueMutate, isDeleting } = useDeleteOptionValue();

    // Handlers
    const handleAddClick = () => {
        if (isCreateMode) return;
        setIsEdit(false);
        setCurrentValue(null);
        setDialogOpen(true);
    };

    const handleEditClick = (value) => {
        setIsEdit(true);
        setCurrentValue(value);
        setDialogOpen(true);
    };

    const handleDeleteClick = (valueId) => {
        setValueToDelete(valueId);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = () => {
        if (valueToDelete) {
            deleteOptionValueMutate(
                { optionGroupId, valueId: valueToDelete },
                {
                    onSuccess: () => {
                        setConfirmDeleteOpen(false);
                        setValueToDelete(null);
                    }
                }
            );
        }
    };

    const handleFormSubmit = (data) => {
        if (isEdit && currentValue?.id) {
            editOptionValue(
                { optionGroupId, valueId: currentValue.id, data },
                { onSuccess: () => setDialogOpen(false) }
            );
        } else {
            createOptionValueMutate(
                { optionGroupId, data },
                { onSuccess: () => setDialogOpen(false) }
            );
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 3,
                borderRadius: 2,
                position: 'relative',
                ...(isCreateMode && {
                    opacity: 0.7,
                    backgroundColor: (theme) => theme.palette.action.hover,
                })
            }}
        >
            {isCreateMode && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(1px)',
                        borderRadius: 2
                    }}
                >
                    <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.main' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                            {isArabic
                                ? 'يجب إضافة مجموعة الخيارات أولاً قبل التمكن من إضافة قيم لها'
                                : 'You must add the option group first before being able to add values to it'}
                        </Typography>
                    </Paper>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {isArabic ? 'قيم الخيار' : 'Option Values'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    size="small"
                    disabled={isCreateMode}
                >
                    {isArabic ? 'إضافة قيمة' : 'Add Value'}
                </Button>
            </Box>

            <OptionValuesTable
                values={isCreateMode ? [] : optionValues}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                displayType={displayType}
                isLoading={isLoading && !isCreateMode}
            />

            <FormAddEditOptionValue
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                currentData={currentValue}
                isEdit={isEdit}
                onSubmit={handleFormSubmit}
                loading={isCreating || isUpdating}
                displayType={displayType}
            />

            <ModelDelete
                confirmOpen={confirmDeleteOpen}
                handleCloseConfirm={() => setConfirmDeleteOpen(false)}
                handleConfirmDelete={handleConfirmDelete}
                isArabic={isArabic}
                isDeletingSingle={isDeleting}
            />
        </Paper>
    );
};

export default OptionValueManager;
